import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePickerField } from "@/components/DatePickerField";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";

const F = ({ label, children }) => <div className="space-y-1"><Label className="text-[11px] font-semibold uppercase tracking-wider text-warmgrey">{label}</Label>{children}</div>;
const sel = "h-10 w-full rounded-md border border-input bg-white px-2 text-sm";

// ID-062: structured mosaic request — colour rules and specs come from the design record, colours from the catalog
export function MosaicRequestDialog({ designId, sourceSku, open, onOpenChange, onCreated }) {
  const [design, setDesign] = useState(null);
  const [colors, setColors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [source, setSource] = useState(null);
  const [slots, setSlots] = useState([]);
  const [f, setF] = useState({ quantity: "", unit: "SQFT", application: "", project_id: "", new_project: "", date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !designId) { return; }
    api.get(`/dealer/mosaic-designs/${designId}`).then(({ data }) => { setDesign(data); setSlots((s) => (s.length ? s : Array.from({ length: data.min_colors }, () => ({ code: "", pct: "" })))); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/catalog?category=zellige").then(({ data }) => {
      const m = {};
      data.forEach((v) => { if (v.color_code && !m[v.color_code]) { m[v.color_code] = v.display_color_name || v.color_name; } });
      setColors(Object.entries(m).sort());
    });
    api.get("/projects").then(({ data }) => setProjects(Array.isArray(data) ? data : data.projects || [])).catch(() => setProjects([]));
    if (sourceSku) {
      api.get(`/catalog/search?q=${encodeURIComponent(sourceSku)}`).then(({ data }) => {
        const v = data[0];
        if (!v) { return; }
        setSource(v);
        setSlots((v.composition || []).map((c) => ({ code: c.code, pct: c.share_pct ?? "" })));
      });
    }
  }, [open, designId, sourceSku]);
  if (!design) { return null; }
  const mode = design.composition_mode;
  const spb = design.sqft_per_box;
  const q = parseFloat(f.quantity) || 0;
  const conv = f.unit === "SQFT" ? `${(q / spb).toFixed(2)} boxes` : `${(q * spb).toFixed(2)} sq ft`;
  const setSlot = (i, k, v) => setSlots((s) => s.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const rule = design.min_colors === design.max_colors ? `exactly ${design.min_colors}` : `${design.min_colors}–${design.max_colors}`;
  const submit = async () => {
    setBusy(true);
    try {
      const allocations = mode === "NONE" ? [] : slots.map((s, i) => ({ color_index: i, pct: parseFloat(s.pct) })).filter((a) => a.pct > 0);
      const { data } = await api.post("/dealer/custom-studio", {
        design_name: `${design.label} — custom colors`, product_type: "Mosaic", project_id: f.project_id || null, new_project_name: f.project_id ? null : f.new_project.trim() || null,
        desired_delivery_date: f.date || null, description: f.notes || null,
        lines: [{ format_mode: "MOSAIC", design_id: designId, source_sku: sourceSku || null, colors: slots.map((s) => ({ code: s.code })), allocations,
          quantity: q, quantity_unit: f.unit, application: f.application, notes: f.notes || null }],
      });
      toast.success(`Request ${data.request_number || ""} sent to Zelligio`);
      onOpenChange(false); onCreated?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white" data-testid="mosaic-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">{design.label} — other colors</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">Choose {rule} colors from the Zelligio catalog. Zelligio reviews feasibility and sends a quote — nothing is ordered yet.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-4 rounded-md border border-border bg-sand/30 p-3 text-xs text-charcoal" data-testid="mosaic-request-spec">
          {source?.image_urls?.[0] && <img src={source.image_urls[0]} alt={source.sku} className="h-16 w-16 rounded object-cover" />}
          <div>
            {source && <p>Based on {source.sku}</p>}
            <p>{[design.tile_size_in && `Tile ${design.tile_size_in}`, design.mesh_sheet_in && `Mesh sheet ${design.mesh_sheet_in}`, design.mounting, design.pattern].filter(Boolean).join(" · ")}</p>
            <p>{spb} sq ft/box{design.sheets_per_box ? ` · ${design.sheets_per_box} sheets/box` : ""}{design.pieces_per_sheet ? ` · ${design.pieces_per_sheet} pcs/sheet` : ""}</p>
          </div>
        </div>
        <div className="space-y-2">
          {slots.map((s, i) => (
            <div key={i} className="flex items-end gap-2" data-testid={`mosaic-slot-${i}`}>
              <div className="flex-1"><F label={`Color ${i + 1}`}>
                <select value={s.code} onChange={(e) => setSlot(i, "code", e.target.value)} className={sel} data-testid={`mosaic-color-${i}`}>
                  <option value="">Choose a color</option>{colors.map(([c, n]) => <option key={c} value={c}>{c} — {n}</option>)}
                </select></F></div>
              {mode !== "NONE" && <div className="w-24"><F label={mode === "REQUIRED" ? "% *" : "%"}><Input type="number" min="0" max="100" value={s.pct} onChange={(e) => setSlot(i, "pct", e.target.value)} data-testid={`mosaic-pct-${i}`} className="h-10 bg-white" /></F></div>}
              {slots.length > design.min_colors && <button type="button" onClick={() => setSlots(slots.filter((_, j) => j !== i))} aria-label="Remove color" data-testid={`mosaic-remove-${i}`} className="mb-2 text-warmgrey hover:text-terracotta"><X className="h-4 w-4" /></button>}
            </div>
          ))}
          {slots.length < design.max_colors && <Button type="button" variant="ghost" size="sm" onClick={() => setSlots([...slots, { code: "", pct: "" }])} data-testid="mosaic-add-color" className="text-terracotta"><Plus className="mr-1 h-4 w-4" /> Add color</Button>}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <F label="Quantity *"><Input type="number" min="0" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="mosaic-quantity" className="h-10 bg-white" /></F>
          <F label="Unit"><select value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className={sel} data-testid="mosaic-unit"><option value="SQFT">sq ft</option><option value="BOXES">boxes</option></select></F>
          <F label="Equals"><p className="h-10 py-2 text-sm text-charcoal" data-testid="mosaic-conversion">{q ? conv : "—"}</p></F>
          <F label="Application *"><Input value={f.application} onChange={(e) => setF({ ...f, application: e.target.value })} placeholder="e.g. Shower wall" data-testid="mosaic-application" className="h-10 bg-white" /></F>
          <F label="Project *"><select value={f.project_id} onChange={(e) => setF({ ...f, project_id: e.target.value })} className={sel} data-testid="mosaic-project"><option value="">New project…</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></F>
          {!f.project_id && <F label="New project name *"><Input value={f.new_project} onChange={(e) => setF({ ...f, new_project: e.target.value })} data-testid="mosaic-new-project" className="h-10 bg-white" /></F>}
          <F label="Needed by"><DatePickerField value={f.date} onChange={(v) => setF({ ...f, date: v })} min="today" testId="mosaic-date" /></F>
        </div>
        <F label="Notes"><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="mosaic-notes" className="bg-white" /></F>
        <Button disabled={busy || !q || f.application.trim().length < 2 || slots.some((s) => !s.code) || (!f.project_id && !f.new_project.trim())} onClick={submit} data-testid="mosaic-submit" className="h-11 bg-terracotta text-white hover:bg-terracotta-hover">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send request"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
